import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { Breadcrumb } from "@/shared/ui/Breadcrumb";
import { Loader } from "@/shared/ui/Loader";

import { useNavigate, useSearch } from "@tanstack/react-router";
import { ShoppingBagIcon } from "lucide-react";
import { useOrdersQuery, useOrdersSummaryQuery } from "../api/queries";
import { OrdersFilter } from "../ui/OrdersFilter";
import { OrdersSummaryCards } from "../ui/OrdersSummaryCards";
import { OrdersTable } from "../ui/OrdersTable";

type OrdersSearch = {
  status?: string;
  branchId?: string;
  channel?: string;
  createdFrom?: string;
  createdTo?: string;
  pickupFrom?: string;
  pickupTo?: string;
  search?: string;
  sortField?: string;
  sortOrder?: "asc" | "desc";
  offset?: number;
  limit?: number;
};

export function OrdersPage() {
  const nav = useNavigate({ from: "/app/orders" });
  const search = useSearch({ from: "/app/orders" }) as OrdersSearch;

  const params = {
    status: search.status,
    branchId: search.branchId,
    channel: search.channel as "APP" | "WALKIN" | undefined,
    createdFrom: search.createdFrom,
    createdTo: search.createdTo,
    pickupFrom: search.pickupFrom,
    pickupTo: search.pickupTo,
    search: search.search,
    sortField: (search.sortField ?? "createdAt") as
      | "createdAt"
      | "pickupAt"
      | "total",
    sortOrder: (search.sortOrder ?? "desc") as "asc" | "desc",
    limit: search.limit ?? 20,
    offset: search.offset ?? 0,
  };

  const { data, isLoading } = useOrdersQuery(params);
  const { data: summaryData, isLoading: summaryLoading } =
    useOrdersSummaryQuery({ branchId: search.branchId });
  const items = data?.items ?? [];
  const totalCount = data?.total ?? 0;

  return (
    <>
      <Header>
        <Breadcrumb
          items={[
            {
              label: "ອໍເດີ (HQ)",
              to: "/app/orders",
            },
          ]}
        />
      </Header>
      <Main>
        <div className="mb-4 flex flex-wrap items-start justify-between gap-2">
          <div>
            <h2 className="flex items-center gap-2 font-bold text-2xl tracking-tight">
              <ShoppingBagIcon className="size-5" />
              ອໍເດີ (HQ)
            </h2>
            <p className="text-muted-foreground">
              ກວດສອບອໍເດີທຸກສາຂາ; ກົດແຖວເພື່ອດູລາຍລະອຽດ ແລະ timeline.
            </p>
          </div>
        </div>

        <div className="mb-4">
          <OrdersSummaryCards
            totalOrdersToday={summaryData?.totalOrdersToday ?? 0}
            pendingConfirmation={summaryData?.pendingConfirmation ?? 0}
            todayRevenue={summaryData?.todayRevenue ?? 0}
            isLoading={summaryLoading}
          />
        </div>

        <div className="flex flex-col rounded-xl border bg-card pt-2">
          <OrdersFilter />

          {isLoading && items.length === 0 ? (
            <div className="flex justify-center py-12">
              <Loader />
            </div>
          ) : (
            <OrdersTable
              data={items}
              isLoading={isLoading}
              offset={params.offset}
              limit={params.limit}
              totalCount={totalCount}
              sortBy={params.sortField}
              sortOrder={params.sortOrder}
              onSortingChange={(id, desc) =>
                nav({
                  search: {
                    ...search,
                    sortField: id,
                    sortOrder: desc ? "desc" : "asc",
                  },
                })
              }
              onPaginationChange={(newOffset, newLimit) =>
                nav({
                  search: {
                    ...search,
                    offset: newOffset,
                    limit: newLimit,
                  },
                })
              }
              onView={(id) => nav({ to: "/app/orders/$id", params: { id } })}
            />
          )}
        </div>
      </Main>
    </>
  );
}
